/**
 * Lift2Fit — block summary.
 *
 * The lifter's primary summary view: the CURRENT four-week block (headline
 * numbers, goal bars, week-by-week) with past blocks kept secondary below it
 * and a compact read-only view of the Grid.
 *
 * Every number is derived from the payload; when a block has no sessions the
 * screen says so instead of dressing zeros up as data.
 */
import { useState } from 'react';
import { api } from '@/lib/api';
import { cn, formatCompactCount, formatCompactVolume, formatDate } from '@/lib/format';
import { formatVolume } from '@/lib/units';
import { useSession } from '@/lib/useSession';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { ArrowRightIcon, BarbellIcon, ChevronDownIcon, GridIcon, TargetIcon, TrendUpIcon } from '@/lib/icons';
import { Button } from '@/components/ui';
import { AppLinkButton, EmptyState, LoadingPanel, MetricTile, Panel, ScreenHeader, StatusChip } from '../components';
import { BlockWeekRows, GoalProgressBar, RecentSetsTable, SourceNotice } from '../blocks';
import { useAsync, useCachedAsync } from '../hooks';
import { Link } from '../router';

function adherenceHintTone(pct: number): 'success' | 'warning' | 'danger' {
  if (pct >= 80) return 'success';
  if (pct >= 60) return 'warning';
  return 'danger';
}

function RecapStat({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
      <span className="l2f-tabular font-mono text-[14px] font-bold text-ink">{value}</span>
    </span>
  );
}

export function BlockSummaryScreen() {
  const session = useSession();
  const { unit } = useUnitPreference();
  const track = session.user?.track ?? 'general';
  const blockState = useCachedAsync(`blocks/current:${track}`, () => api.blocksCurrent(), [track]);
  const historyState = useCachedAsync(`blocks/history:${track}`, () => api.blocksHistory(6), [track]);
  const setsState = useCachedAsync(`sets/recent:${track}`, () => api.recentSets(5), [track]);
  const [expanded, setExpanded] = useState<number | null>(null);

  const data = blockState.data;
  const history = historyState.data?.blocks ?? [];
  const recentSets = setsState.data ?? [];

  if (blockState.loading && !data) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading your block" rows={4} />
        <LoadingPanel label="Loading goal progress" rows={3} />
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        title="We couldn't load your block"
        body={
          blockState.error ??
          'The API did not answer. Your own block was not replaced with demo numbers — try again.'
        }
        icon={<BarbellIcon className="h-8 w-8 text-accent" />}
        action={
          <Button onClick={blockState.reload} data-testid="block-retry">
            Try again
          </Button>
        }
      />
    );
  }

  const { block, totals, weeks, goals } = data;
  // No sessions and no sets means an empty block: show the honest empty state.
  const blockIsEmpty = totals.sessions === 0 && totals.sets === 0;

  const header = (
    <div data-testid="block-headline">
      <ScreenHeader
        eyebrow="Block summary"
        title={`Block ${block.index}`}
        badge={
          <StatusChip tone="accent">
            Week {block.weekIndex} of {block.weeks}
          </StatusChip>
        }
        lead={`${formatDate(block.startsOn)} – ${formatDate(block.endsOn)} · four ISO weeks, Monday start.`}
        actions={
          <Link
            to="/goals"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            <TargetIcon className="text-[14px]" />
            Block goals
            <ArrowRightIcon />
          </Link>
        }
      />
    </div>
  );

  const goalsPanel = (
    <Panel
      title="Goal achievement"
      subtitle={`${goals.length} of 5 goals set for this block.`}
      actions={
        <Link
          to="/goals"
          className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
        >
          Edit goals
          <ArrowRightIcon />
        </Link>
      }
    >
      {goals.length ? (
        <ul className="flex flex-col">
          {goals.map((goal) => (
            <GoalProgressBar key={goal.id} goal={goal} />
          ))}
        </ul>
      ) : (
        <EmptyState
          title="No goals for this block yet"
          body="Add up to five goals and each one tracks itself from your log — sessions, volume, an exercise e1RM, bodyweight and more."
          icon={<TargetIcon className="h-8 w-8 text-accent" />}
          action={
            <Link
              to="/goals"
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              Set a goal
              <ArrowRightIcon />
            </Link>
          }
        />
      )}
    </Panel>
  );

  const gridPanel = (
    <Panel
      title="Your grid"
      subtitle="The most recent sets logged in this block — movement, weight, reps, RPE."
      actions={
        <AppLinkButton to="/grid" variant="outline" size="sm">
          <GridIcon className="text-sm" />
          Open The Grid
        </AppLinkButton>
      }
    >
      {setsState.error && !recentSets.length ? (
        <p data-testid="grid-error" className="text-[13px] text-danger">
          {setsState.error} Your logged sets were not replaced with demo data.
        </p>
      ) : recentSets.length ? (
        <RecentSetsTable sets={recentSets} unit={unit} />
      ) : (
        <p className="text-[13px] text-ink-muted">No sets logged in this block yet.</p>
      )}
    </Panel>
  );

  const historyPanel = (
    <Panel title="Past blocks" subtitle="Newest first. Expand a block for its week rows.">
      {historyState.error && !history.length ? (
        <div data-testid="history-error" className="flex flex-col items-start gap-3">
          <p className="text-[13px] text-danger">{historyState.error}</p>
          <Button variant="outline" size="sm" onClick={historyState.reload} data-testid="history-retry">
            Try again
          </Button>
        </div>
      ) : historyState.loading && !history.length ? (
        <LoadingPanel label="Loading past blocks" rows={3} />
      ) : history.length ? (
        <ul className="flex flex-col">
          {history.map((entry) => {
            const isOpen = expanded === entry.index;
            return (
              <li key={entry.index} data-testid="history-block" className="border-b border-hairline last:border-b-0">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setExpanded(isOpen ? null : entry.index)}
                  className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3 text-left"
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] font-bold uppercase tracking-[0.06em] text-ink">
                      Block {entry.index}
                    </span>
                    <span className="text-[12px] text-ink-muted">
                      {formatDate(entry.startsOn)} – {formatDate(entry.endsOn)}
                    </span>
                  </span>
                  <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink-muted">
                    <span>
                      <span className="l2f-tabular font-mono font-bold text-ink">{entry.sessions}</span> /{' '}
                      {entry.targetSessions} sessions
                    </span>
                    <span>
                      <span className="l2f-tabular font-mono font-bold text-ink">{entry.adherencePct}%</span> adherence
                    </span>
                    <span>
                      <span className="l2f-tabular font-mono font-bold text-ink">{entry.goalsMet}</span> of{' '}
                      {entry.goalsTotal} goals met
                    </span>
                    <ChevronDownIcon
                      className={cn('text-[14px] transition-transform duration-150', isOpen && 'rotate-180')}
                    />
                  </span>
                </button>

                {isOpen ? (
                  <div className="flex flex-col gap-3 pb-4">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-hairline pt-3 sm:grid-cols-4">
                      <RecapStat label="Sessions" value={`${entry.sessions} / ${entry.targetSessions}`} />
                      <RecapStat label="Sets" value={String(entry.sets)} />
                      <RecapStat label="Volume" value={formatVolume(entry.volumeKg, unit)} />
                      <RecapStat label="Adherence" value={`${entry.adherencePct}%`} />
                    </div>
                    {entry.weeks?.length ? <BlockWeekRows weeks={entry.weeks} unit={unit} /> : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          title="No completed blocks yet"
          body="Your first four-week block is still open. Its history lands here once it closes."
          icon={<TrendUpIcon className="h-8 w-8 text-accent" />}
        />
      )}
    </Panel>
  );

  if (blockIsEmpty) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <SourceNotice source={blockState.source} isDemoSession={session.isDemo} />
        <div data-testid="block-empty">
          <EmptyState
            title="No sessions yet in this block"
            body="Log your first set and this screen fills in — sessions against target, volume, adherence, your goals and the week-by-week breakdown."
            icon={<BarbellIcon className="h-8 w-8 text-accent" />}
            action={
              <AppLinkButton to="/grid" size="lg">
                Log your first set
                <ArrowRightIcon />
              </AppLinkButton>
            }
          />
        </div>
        {goalsPanel}
        {historyPanel}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}
      <SourceNotice source={blockState.source} isDemoSession={session.isDemo} />

      <section aria-label="Block totals" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <MetricTile
          label="Sessions"
          value={formatCompactCount(totals.sessions)}
          valueTitle={String(totals.sessions)}
          hint={`of ${totals.targetSessions} planned`}
          icon={<BarbellIcon />}
        />
        <MetricTile label="Sets" value={formatCompactCount(totals.sets)} valueTitle={String(totals.sets)} hint="working sets logged" />
        <MetricTile
          label="Volume"
          value={formatCompactVolume(totals.volumeKg, unit)}
          valueTitle={formatVolume(totals.volumeKg, unit)}
          hint="total load moved"
        />
        <MetricTile label="Active days" value={formatCompactCount(totals.activeDays)} valueTitle={String(totals.activeDays)} hint={`of ${block.weeks * 7} days`} />
        <MetricTile
          label="Adherence"
          value={`${totals.adherencePct}%`}
          hint={`${totals.sessions} of ${totals.targetSessions} sessions`}
          hintTone={adherenceHintTone(totals.adherencePct)}
        />
      </section>

      {goalsPanel}

      <Panel
        title={`Block ${block.index} · week by week`}
        subtitle="Sessions against target and adherence, oldest first. The current week is marked."
      >
        <BlockWeekRows weeks={weeks} unit={unit} />
      </Panel>

      {gridPanel}

      {historyPanel}
    </div>
  );
}
